import { z } from "zod";

export const TEMPLATE_STORAGE_KEY = "rh.emails.builder.templates.v1";

const templateRecordSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1).max(80),
  projectData: z.unknown(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

const templateArraySchema = z.array(templateRecordSchema);

export type TemplateRecord = z.infer<typeof templateRecordSchema>;
export type TemplateSummary = Pick<
  TemplateRecord,
  "id" | "name" | "updatedAt"
>;

function readAll(): TemplateRecord[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(TEMPLATE_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    const result = templateArraySchema.safeParse(parsed);
    return result.success ? result.data : [];
  } catch {
    return [];
  }
}

function writeAll(records: TemplateRecord[]): void {
  window.localStorage.setItem(TEMPLATE_STORAGE_KEY, JSON.stringify(records));
}

export function listTemplates(): TemplateSummary[] {
  return readAll()
    .map(({ id, name, updatedAt }) => ({ id, name, updatedAt }))
    .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
}

export function getTemplate(id: string): TemplateRecord | null {
  return readAll().find((t) => t.id === id) ?? null;
}

export function saveTemplate(input: {
  id?: string;
  name: string;
  projectData: unknown;
}): TemplateRecord {
  const records = readAll();
  const now = new Date().toISOString();
  const trimmedName = input.name.trim();

  if (input.id) {
    const idx = records.findIndex((t) => t.id === input.id);
    if (idx >= 0) {
      const updated: TemplateRecord = {
        ...records[idx],
        name: trimmedName,
        projectData: input.projectData,
        updatedAt: now,
      };
      records[idx] = updated;
      writeAll(records);
      return updated;
    }
  }

  const created: TemplateRecord = {
    id: crypto.randomUUID(),
    name: trimmedName,
    projectData: input.projectData,
    createdAt: now,
    updatedAt: now,
  };
  records.push(created);
  writeAll(records);
  return created;
}

export function deleteTemplate(id: string): void {
  const records = readAll().filter((t) => t.id !== id);
  writeAll(records);
}
